import api from '../request'
import {
  SYSTEM_BASE_URL,
  CHAT_ENDPOINTS,
  ANALYTICS_BASE_URL,
} from '../../const/EndPoints'
import { ConversationEntry } from '../../models/ConversationEntry'
import LocalStorageService from '../localStorageService'
import { generateUUID } from '../../utils/uuid'
import {
  fetchEventSource,
  EventStreamContentType,
} from '@microsoft/fetch-event-source'

export interface ImageOutput {
  url: string
  prompt?: string
  model?: string
}

export interface SourceCitation {
  id?: number
  title: string
  url: string
}

export interface ChatResponse {
  sessionId: string
  // Stable per-assistant-message UUID issued by the backend. Use this to
  // address per-message endpoints (clip, reactions, quote-reply). Absent
  // on the short-circuit greeting path for legacy clients.
  message_id?: string
  message: string
  images?: ImageOutput[]
  follow_ups?: string[]
  sources?: SourceCitation[]
}

export interface ChatHistoryEntry {
  id?: string
  role: string
  content: string
  timestamp?: string
  images?: ImageOutput[]
  sources?: SourceCitation[]
  follow_ups?: string[]
}

interface ChatHistoryResponse {
  history: ChatHistoryEntry[]
}

export interface PromptSuggestion {
  icon: string
  label: string
  text: string
}

interface PromptSuggestionsResponse {
  prompts: PromptSuggestion[]
  personalized: boolean
}

// Submit chat data
export const submitChatRequest = async (
  payload: FormData
): Promise<ChatResponse> => {
  const response = await api.post<ChatResponse>(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.SUBMIT_CHAT,
    payload
  )

  return response.data
}

// Non-retriable errors — do not reconnect on 4xx (except 429)
class FatalSSEError extends Error {
  status: number
  details?: unknown
  constructor(status: number, message: string, details?: unknown) {
    super(message)
    this.status = status
    this.details = details
  }
}

export interface SSECallbacks {
  onStatus: (message: string) => void
  onTextDelta?: (content: string) => void
  onTextClear?: () => void
}

// Submit chat via SSE — streams real-time status events from the agent
export const submitChatRequestSSE = async (
  payload: FormData,
  callbacks: SSECallbacks,
  signal?: AbortSignal
): Promise<ChatResponse> => {
  const accessToken = LocalStorageService.getItem('accessToken')

  return new Promise<ChatResponse>((resolve, reject) => {
    let result: ChatResponse | null = null

    fetchEventSource(SYSTEM_BASE_URL + CHAT_ENDPOINTS.SUBMIT_CHAT, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'X-Request-Timestamp': Math.floor(Date.now() / 1000).toString(),
        'X-Idempotency-Key': generateUUID(),
      },
      body: payload,
      signal,

      async onopen(response) {
        if (
          response.ok &&
          response.headers.get('content-type')?.includes(EventStreamContentType)
        ) {
          return // connected
        }

        // Try to parse error envelope from non-SSE response
        const body = await response.json().catch(() => null)
        const envelope = body?.error

        if (response.status === 429) {
          throw new FatalSSEError(
            429,
            envelope?.message || 'daily_limit_reached',
            { resets_at: envelope?.details?.resets_at ?? body?.resets_at }
          )
        }

        if (response.status >= 400 && response.status < 500) {
          throw new FatalSSEError(
            envelope?.code || response.status,
            envelope?.message || body?.message || response.statusText,
            envelope?.details
          )
        }

        // 2xx but the server didn't speak SSE. Never retry — otherwise the
        // parser errors, `onerror` returns undefined, and fetch-event-source
        // re-POSTs on a tight loop. Previously reproduced in prod when the
        // greeting short-circuit returned plain JSON against an SSE request
        // and pinned an iOS session at ~1 req/s indefinitely.
        if (response.ok) {
          throw new FatalSSEError(
            response.status,
            'Server returned a non-SSE response to an SSE request'
          )
        }

        // 5xx — let fetchEventSource retry automatically
        throw new Error(envelope?.message || response.statusText)
      },

      onmessage(msg) {
        if (!msg.event || !msg.data) return

        if (msg.event === 'status') {
          const parsed = JSON.parse(msg.data)
          callbacks.onStatus(parsed.message)
          return
        }

        if (msg.event === 'text-delta') {
          if (callbacks.onTextDelta) {
            const parsed = JSON.parse(msg.data)
            callbacks.onTextDelta(parsed.content)
          }
          return
        }

        if (msg.event === 'text-clear') {
          callbacks.onTextClear?.()
          return
        }

        if (msg.event === 'response') {
          const parsed = JSON.parse(msg.data)
          result = parsed.data ?? parsed
          return
        }

        if (msg.event === 'error') {
          const parsed = JSON.parse(msg.data)
          throw new FatalSSEError(
            parsed.code || 500,
            parsed.error || parsed.message
          )
        }
      },

      onclose() {
        if (result) {
          resolve(result)
        } else {
          reject({ status: 500, message: 'Stream ended without a response' })
        }
      },

      onerror(err) {
        if (err instanceof FatalSSEError) {
          reject({
            status: err.status,
            message: err.message,
            error: err.status === 429 ? 'daily_limit_reached' : undefined,
            resets_at: (err.details as any)?.resets_at,
            details: err.details,
          })
          throw err // stop retrying
        }
        // Returning nothing lets fetchEventSource retry with backoff
      },

      // Agent requests take 30-60s — users will tab away. Keep the stream alive.
      openWhenHidden: true,
    })
  })
}

// Fetch chat history for a session
export const getChatHistory = async (
  sessionId: string
): Promise<ChatHistoryResponse> => {
  const response = await api.get<ChatHistoryResponse>(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.GET_CHAT_HISTORY(sessionId)
  )
  return response.data
}

// Fetch personalized prompt suggestions.
// No AbortSignal: the server-side LLM call is short (~10s ceiling), idempotent,
// and the inference cost is paid the moment the request lands. Letting it
// complete delivers personalised content; cancelling on a re-render just
// burns the call and falls back to defaults.
export const getPromptSuggestions = async (): Promise<PromptSuggestionsResponse> => {
  const response = await api.get<PromptSuggestionsResponse>(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.PROMPT_SUGGESTIONS
  )
  return response.data
}

export interface ConversationHistoryResponse {
  conversations: ConversationEntry[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export const getConversationHistory = async (
  page: number,
  pageSize: number,
  startDate?: string,
  endDate?: string
): Promise<ConversationHistoryResponse> => {
  const url = `${ANALYTICS_BASE_URL}${CHAT_ENDPOINTS.GET_USER_CONVERSATIONS(
    page,
    pageSize,
    startDate,
    endDate
  )}`

  const response = await api.get<ConversationHistoryResponse>(url)
  return response.data
}

// ─── Walk With Me — live follow-along sharing ───

export interface WalkShareResponse {
  url: string
  token: string
  expires_at: string
}

export interface ClipShareResponse extends WalkShareResponse {}


// Create a live follow-along link for the host's current chat session.
// Envelope: backend returns { data: { token, url, expires_at } } — the
// singleton axios interceptor strips the outer response wrapper, so
// `response.data` here is the inner `data` object.
export const createWalkLink = async (
  sessionId: string,
  hostFirst?: string
): Promise<WalkShareResponse> => {
  const response: any = await api.post(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.CREATE_WALK(sessionId),
    hostFirst ? { host_first: hostFirst } : {}
  )
  return response.data as WalkShareResponse
}

export const revokeWalkLink = async (token: string): Promise<void> => {
  await api.delete(SYSTEM_BASE_URL + CHAT_ENDPOINTS.REVOKE_WALK(token))
}

// Clip a single AI answer into a 7-day expiring link. The messageId comes
// from the assistant message's server-issued UUID; the backend resolves the
// session, question, answer, and sources from Redis. The body only carries
// the optional display name the recipient page shows in the footer.
export const createClipLink = async (
  messageId: string,
  hostFirst?: string
): Promise<ClipShareResponse> => {
  const response: any = await api.post(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.CREATE_CLIP(messageId),
    hostFirst ? { host_first: hostFirst } : {}
  )
  return response.data as ClipShareResponse
}

export const revokeClipLink = async (token: string): Promise<void> => {
  await api.delete(SYSTEM_BASE_URL + CHAT_ENDPOINTS.REVOKE_CLIP(token))
}
